import { Component, OnDestroy, OnInit } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { BudgetService } from '../../services/budget/budget.service';
import { Subscription } from 'rxjs';
import { Budget, BudgetNotificationResponse, BudgetObject, BudgetRecord, LoginApiModelForBudget } from '../../models/budget.model';
import { AngularAssignToolService } from 'src/app/reusables/assign-tool/angular-assign-tool.service';
import { DialogService } from 'primeng/dynamicdialog';
import { CvCxLoginAssignToolOptions, LoginAssignTool } from 'src/app/reusables/assign-tool/cxone-logins-assign-tool/cxone-logins-assign-tool.model';
import { ApiResponse } from 'src/AngularJS/Models/api-response.model';
import { CxoneLoginsAssignToolComponent } from 'src/app/reusables/assign-tool/cxone-logins-assign-tool/cxone-logins-assign-tool.component';
import { TypeFormats } from 'src/shared/types/exportItem';
import { MenuItem } from '@niceltd/sol/floating-menu/src/lib/menu-items';
import { columnList, headerItemsNotification } from './export-budget-notification';

@Component({
    selector: 'cv-admin-budget-notifications',
    templateUrl: './cv-admin-budget-notifications.component.html',
    styleUrl: './cv-admin-budget-notifications.component.scss',
    providers: [DialogService]
})
export class CvAdminBudgetNotificationsComponent implements OnInit, OnDestroy {
    private budgetSubscription: Subscription;
    isLoading = false;
    savingAlertAt = false;

    budget: Budget = {
        budgetList: [],
        total: "",
        initBudgetList: [],
        AlertAt: 0,
        areNotificationsActive: false,
        selectedNotificationBudget: null
    };

    selectedOptions = {
        yes: 'Yes',
        no: 'No',
        notApplicable: 'N/A'
    };
    
    fileName: string = "";

    alertValues = [
        { name: '25%', selected: false, value: 1 },
        { name: '50%', selected: false, value: 2 },
        { name: '75%', selected: false, value: 4 },
        { name: '100%', selected: false, value: 8 }
    ];

    defaultMenuOptions: MenuItem[] = [
        {
          label: "Excel",
          svgIconName: "icon-office-excel",
          callbackFn: this.onExportClick.bind(this),
        },
        {
          label: "CSV",
          svgIconName: "icon-CSV",
          callbackFn: this.onExportClick.bind(this),
        }
    ];

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private budgetService: BudgetService,
        private angularAssignToolService: AngularAssignToolService,
        private dialogSrc: DialogService,
    ) { }

    async ngOnInit() {
        this.budgetSubscription = this.budgetService.budgetList$.subscribe(budgetList => {
            if (budgetList.length > 0) {
                const mappedList = budgetList.map(budget => ({
                    ...budget,
                    AmountString: budget.Amount.toString(),
                    NotifyLogins: budget.NotifyLogins || []
                } as BudgetRecord))
                    .sort((a, b) => a.GroupLevelName.localeCompare(b.GroupLevelName, undefined, { sensitivity: 'base' }));

                this.budget.budgetList = mappedList;
                this.budget.initBudgetList = [...mappedList];

                this.initialize();
            }
        });
    }

    ngOnDestroy() {
        this.budgetSubscription?.unsubscribe();
    }

    get areNotificationsActive(): boolean {
        return this.budget.areNotificationsActive ?? false;
    }
    
    set areNotificationsActive(value: boolean) {
        this.budget.areNotificationsActive = value;
    }

    get sectionTitle(): string {
        return this.getLocalizationString("Budget_Notifications", "Budget Notifications");
    }

    get groupName(): string {
        return this.budget.budgetList[0].GroupLevel === 2 ? this.getLocalizationString("Team", "Team") : this.getLocalizationString("Company", "Company");
    }

    private async initialize() {
        try {
            this.isLoading = true;
            await this.loadLocalization(),
            await this.loadBudgetNotifications()
        } finally {
            this.isLoading = false;
        }
    }

    async loadLocalization() {
        await this.cvLocalizationService.GetSection("Budget_Notifications");
        this.selectedOptions.yes = await this.cvLocalizationService.GetLocalText("Common", "Yes", "Yes");
        this.selectedOptions.no = await this.cvLocalizationService.GetLocalText("Common", "No", "No");
        this.fileName = await this.cvLocalizationService.GetLocalText("Budget", "Budget_Notification", "Budget Notifications");
    }

    getValuesNotification = (alertValues, budgetSetupObject, selectedOptions) =>{
        const updateHeaderName = (headerName, index) => {
            if(!!headerItemsNotification(CVCultureName, this.groupName)[index]?.cells[0]?.text){
                headerItemsNotification(CVCultureName, this.groupName)[index].cells[0].text = headerName;
            }
        };

        updateHeaderName("Team", headerItemsNotification(CVCultureName, this.groupName).length - 1);

        const getAlertValue = ({ isActive } = { isActive: false}) => {
            const dataAlert = alertValues.reduce((acc, av) => {
                const selected = av.selected ? selectedOptions.yes: selectedOptions.no;
                return {
                    ...acc,
                    [av.name]: isActive ? selected : selectedOptions.notApplicable,
                }
            }, {});
            return dataAlert;  
        }

        const groupValues = budgetSetupObject.budgetList.sort((a, b) => {
            if (a.GroupLevelName.toUpperCase() < b.GroupLevelName.toUpperCase()) {
                return -1;
            } else if (a.GroupLevelName.toUpperCase() > b.GroupLevelName.toUpperCase()) {
                return 1;
            } else {
                return 0;
            }
        }).flatMap(b=> {
            const values = b.NotifyLogins && b.NotifyLogins.length > 0 ? b.NotifyLogins.map(notify => ({
                Teams: '',
                Agents: notify?.LoginName || '',
                ...getAlertValue(b)
            })): [
                {
                    Teams: '',
                    Agents: '                 ',
                    ...getAlertValue()
                }
            ];
            return [{
                Teams: b.GroupLevelName,
            }, ...values];
        });
        return groupValues;
    }

    async onExportClick(item: MenuItem) {
        const formatType: TypeFormats = item.label === "Excel" ? "excel" : "csv";
        const body = {
          columnList: columnList(this.alertValues),
          headerItems: headerItemsNotification(CVCultureName, this.groupName),
          values: this.getValuesNotification(this.alertValues, this.budget, this.selectedOptions),
          format: formatType,
          fileNameExport: {
            "name": "Budget_Notification",
            "section": "Budget"
          },
          fileName: this.fileName,
          notification: {
            "section": "Budget",
            "field": "Budget_Notification",
            "defaultMessage": "Budget Notifications"
          },
        }
    
        await this.budgetService.exportBudgetDefault(formatType, body, this.fileName);
        
    }

    getLocalizationString(key: string, defaultValue: string) {
        return this.cvLocalizationService.getCachedText("Budget_Notifications", key, defaultValue);
    }

    async changeNotificationStatus(budgetGroup: BudgetRecord): Promise<void> {
        if (budgetGroup.isEmpty) return;

        budgetGroup.isSaving = true;
        try {
            await this.budgetService.setToggleNotificationStatus(
                budgetGroup.BudgetID,
                budgetGroup.isActive
            );
        } catch (error) {
            console.error('Failed to change notification status: ', error);
            throw error
        } finally {
            budgetGroup.isSaving = false;
        }
    }

    async changeAllNotificationStatus(): Promise<void> {
        this.savingAlertAt = true;
        try {
            await this.budgetService.setToggleNotificationStatus(0, this.areNotificationsActive);
    
            if (this.areNotificationsActive) {
                this.budget.budgetList.forEach(budget => {
                    if (!budget.NotifyLogins?.length) {
                        return;
                    }
                    budget.isActive = true;
                });
            }
        } catch (error) {
            console.error('Failed to change all notification status: ', error);
            throw error;
        } finally {
            this.savingAlertAt = false;
        }
    }

    async loadBudgetNotifications() {
        try {
            const budgetNotificationLoginsList = await this.budgetService.loadBudgetNotificationLoginList();

            if (budgetNotificationLoginsList?.length > 0) {
                // Set AlertAt and update checkboxes
                this.budget.AlertAt = budgetNotificationLoginsList[0].AlertAt;
                let percentLimit = Math.pow(2, 4);
                for (let i = 1, n = 0; i < percentLimit; i *= 2, n++) {
                    this.alertValues[n].selected = (this.budget.AlertAt & i) === i;
                }

                // Group notifications by BudgetID
                const notificationsByBudgetId = new Map<number, BudgetNotificationResponse[]>();
                budgetNotificationLoginsList.forEach(notification => {
                    if (!notificationsByBudgetId.has(notification.BudgetID)) {
                        notificationsByBudgetId.set(notification.BudgetID, []);
                    }
                    notificationsByBudgetId.get(notification.BudgetID)?.push(notification);
                });

                // Update existing budget list
                this.budget.budgetList.forEach(budget => {
                    const notifications = notificationsByBudgetId.get(budget.BudgetID);
                    if (notifications?.length) {
                        budget.NotifyLogins = notifications
                            .filter(n => n.LoginObject.Active)
                            .map(n => ({
                                ...n.LoginObject,
                                Active: n.Active
                            }));
                        budget.isEmpty = budget.NotifyLogins.length === 0;
                        budget.isActive = notifications.some(n => n.Active);
                    }
                });
            }

            // Update global notification state
            this.areNotificationsActive = budgetNotificationLoginsList.some(item => item.Active);
        } catch (error) {
            console.error('Failed to load budget notifications:', error);
            throw error;
        }
    }

    async setAlertAtPercentage(): Promise<void> {
        this.savingAlertAt = true;
        let alertAtValue = 0;

        this.alertValues.forEach(alertValue => {
            if (alertValue.selected) alertAtValue |= alertValue.value;
        });

        this.budget.AlertAt = alertAtValue;

        try {
            await this.budgetService.setNotificationAlertAt(alertAtValue);
        } catch (error) {
            console.error('Failed to set alert percentage:', error);
            throw error
        } finally {
            this.savingAlertAt = false;
        }
    }

    executeGetLoginsByParentGroup = (groupLevel: number, groupType: number, groupId: number): Promise<ApiResponse<LoginAssignTool[]>> =>
        this.angularAssignToolService.loadLoginsByParentGroup(groupLevel, groupType, groupId)
            .then(logins => ({
                data: logins.map(login => ({
                    LoginID: login.LoginID,
                    LoginName: login.LoginName,
                    FirstName: login.FirstName,
                    LastName: login.LastName,
                    CompanyID: login.CompanyID,
                    ProfileID: login.ProfileID,
                    Active: login.Active,
                    RelatedGroupLevel: login.RelatedGroupLevel,
                    RelatedGroupType: login.RelatedGroupType,
                    RelatedGroupID: login.GroupID
                })) as LoginAssignTool[]
            }) as ApiResponse<LoginAssignTool[]>);

    openNotificationAssignTool(budgetGroup: BudgetRecord) {
        const options: CvCxLoginAssignToolOptions = {
            getLogins: () => this.executeGetLoginsByParentGroup(
                budgetGroup.GroupLevel,
                0,
                budgetGroup.EmpGroupID
            ),
            submitLogins: async (logins: LoginAssignTool[]) => {
                const budgetId = budgetGroup.BudgetID;
                const loginsList = logins.map(login => ({
                    LoginID: login.LoginID,
                    CompanyID: login.CompanyID,
                    LoginName: login.LoginName,
                    GroupID: login.RelatedGroupID,
                    RelatedGroupLevel: login.RelatedGroupLevel,
                    RelatedGroupType: login.RelatedGroupType,
                    ProfileID: login.ProfileID,
                    Active: true
                } as LoginApiModelForBudget));

                // Update the budget group directly
                budgetGroup.NotifyLogins = loginsList;
                budgetGroup.isEmpty = loginsList.length === 0;
                budgetGroup.isActive = !budgetGroup.isEmpty;

                await this.budgetService.saveBudgetNotifications(budgetId, this.budget.AlertAt ?? 0, loginsList);
                await this.loadBudgetNotifications();
                return { data: logins } as ApiResponse<LoginAssignTool[]>;
            },
            headerTitle: this.getLocalizationString('Select_Recipients', 'Select Recipients'),
            saveLabel: this.getLocalizationString('Assign', 'Assign'),
            cancelLabel: this.getLocalizationString('Cancel', 'Cancel'),
            selectedLogins: budgetGroup.NotifyLogins?.map(login => ({
                LoginID: login.LoginID,
                LoginName: login.LoginName,
                CompanyID: login.CompanyID,
                ProfileID: login.ProfileID,
                Active: login.Active,
                RelatedGroupLevel: login.RelatedGroupLevel,
                RelatedGroupType: login.RelatedGroupType,
                RelatedGroupID: login.GroupID
            } as LoginAssignTool)) ?? [],
            alwaysEnableSave: true,
            hideColumns: ['ParentGroupName']
        };

        this.dialogSrc.open(CxoneLoginsAssignToolComponent, {
            width: "775px",
            height: "calc(100% - 50px)",
            baseZIndex: 10000,
            styleClass: 'cx-one-ui budget-notifications-recipients-modal btn-bg-is-white admin-budget-notifications-hide-team-column',
            data: { options }
        });
    }
}